import React, { Fragment } from 'react'
import { metrics, operators } from './common'
import { Popover } from '../ui/popover'
interface ErrorWindowProps {
    error: ParsingErr
    value: string
}

export const ErrorWindow = ({ error, value }: ErrorWindowProps) => {
    switch (error.type) {
        case 'parsing_error':
            return <ParsingError error={error} value={value} />
        default:
            return <div>Unknown error. {error?.message ? error.message : null}</div>
    }
}

interface ParsingErr {
    expected: string
    found: string
    pos: number
    type: string
    code: number
    message: string
}

const ParsingError = ({ error, value }: ErrorWindowProps) => {
    const lines = value.split('\n')

    let errLine = -1
    let lineStart = 0 // Character position where current line starts (in original string)

    for (let i = 0; i < lines.length; i++) {
        const lineEnd = lineStart + lines[i].length

        if (error.pos <= lineEnd) {
            errLine = i
            break
        }

        // Move to next line (+1 for the newline character that split() removed)
        lineStart = lineEnd + 1
    }

    const column = error.pos - lineStart
    const spaces = '-'.repeat(Math.max(0, column))
    console.log('err line', errLine, 'column', column, 'original pos', error.pos, 'for', error)

    let expectedButFoundText = null
    let expectedText = expected(error.expected)
    if (expectedText || error.found) {
        expectedButFoundText = `. Expected ${expectedText} but found ${error.found}`
    }
    let lineNumberText = ''
    if (errLine !== -1) {
        lineNumberText = ` on line ${errLine + 1}`
    }

    return (
        <div>
            <div>
                Parsing Error{expectedButFoundText}
                {lineNumberText}. <ErrorCode error={error} />
            </div>
            {!!error.message && <div>{error.message}</div>}
            {lines.map((l, i) => {
                if (i !== errLine) {
                    return null
                }

                return (
                    <Fragment key={i}>
                        <div>
                            <code className="text-mjDark-1400" style={{ whiteSpace: 'pre' }}>
                                {l}
                            </code>
                        </div>
                        <div className="text-mjDark-1400">
                            {i === errLine && (
                                <code className="text-mjDark-1400" style={{ whiteSpace: 'pre' }}>
                                    {spaces + '^'}
                                </code>
                            )}
                        </div>
                    </Fragment>
                )
            })}
        </div>
    )
}

const fieldPopover = (
    <div className="p-2">
        A field can be one of the following
        <ul className="pl-4 list-disc mb-0">
            {metrics.map((m, i) => (
                <li key={i}>{m}</li>
            ))}
        </ul>
    </div>
)

const operatorPopover = (
    <div className="p-2">
        <div className="mb-4">
            For a numeric value, an operator can be one of the following:
            <ul className="pl-4 list-disc mb-0">
                {operators.map((m, i) => (
                    <li key={i}>{m}</li>
                ))}
            </ul>
        </div>

        <div>
            For a string value, an operator can only be:
            <ul className="pl-4 list-disc mb-0">
                <li>=</li>
                <li>!=</li>
            </ul>
        </div>
    </div>
)

const valuePopover = (
    <div className="p-2">
        A value can be either "enabled", "active", "running", "paused", "archived", or any number
        with (or without) decimal places. Numbers can include $ and % signs, but cannot include
        commas.
    </div>
)

const popovers = {
    field: fieldPopover,
    operator: operatorPopover,
    value: valuePopover,
}

const expected = (expected: string): JSX.Element | string => {
    switch (expected) {
        case 'field':
        case 'operator':
        case 'value':
            return (
                <Popover interactionKind="hover" content={popovers[expected]}>
                    <span className="underline cursor-pointer">{expected}</span>
                </Popover>
            )
        default:
            return expected
    }
}

enum ErrorCodes {
    MixedAndOr = 1,
}

const ErrorCode = ({ error }: { error: ParsingErr }) => {
    if (!error.code) {
        return null
    }

    switch (error.code) {
        case ErrorCodes.MixedAndOr:
            return <MixedAndOr />
        default:
            console.warn('Unhandled error code from', error)
            return null
    }
}

const MixedAndOr = () => {
    const content = (
        <div className="p-2" style={{ maxWidth: '400px' }}>
            <div className="mb-2">
                You are not allowed to have mixed "and" / "or" statements on the same level.
            </div>

            <div>If you had something like</div>
            <div className="mb-2">
                <code>over the last 30 days spend &gt; 10 and sales = 0 or clicks &lt; 100</code>
            </div>

            <div>
                You'll want to group those using parenthesis so that you are only using one type of
                "and" / "or" for each group. Like this:
            </div>
            <div>
                <code>over the last 30 days (spend &gt; 10 and sales = 0) or clicks &lt; 100</code>
            </div>
        </div>
    )

    return (
        <Popover content={content} interactionKind="hover">
            <span className="underline cursor-pointer">
                It looks like you have mixed "and" / "or" statements.
            </span>
        </Popover>
    )
}
